/**
 * Every DSP threshold in the project lives here. Nothing in `src/audio` may
 * contain a bare numeric constant that a human would want to tune.
 *
 * Values were chosen from the literature cited in docs/research.md and then
 * checked against the synthetic test suite; they are starting points for real
 * guitar tuning, not sacred numbers.
 */

export const CAPTURE = {
  /** Samples per message from the AudioWorklet. 512 @48k = 10.7 ms. */
  hopSize: 512,
  /** Above this absolute sample value we call the frame clipped. */
  clipThreshold: 0.985,
  /** Requested constraints. Safari may silently ignore some of these. */
  constraints: {
    channelCount: 1,
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  } as const,
} as const;

export const ONSET = {
  /** FFT over the native-rate signal. 1024 @48k = 21 ms window. */
  fftSize: 1024,
  /** Bins above this frequency are ignored — mostly pick/finger noise. */
  maxFrequencyHz: 5000,
  /** Bins below this are ignored — room rumble and handling noise. */
  minFrequencyHz: 60,
  /** Adaptive whitening: per-bin peak decays by this factor each frame. */
  whiteningDecay: 0.9971,
  /** Absolute minimum whitening floor, so silent bins cannot explode. */
  whiteningFloor: 0.002,
  /**
   * The per-bin whitening floor is the measured room noise times this margin,
   * so a bin containing only hiss is never amplified into apparent activity.
   */
  noiseFloorMargin: 1.6,
  /** Frames averaged into the detection function before peak picking. */
  smoothingFrames: 3,
  /**
   * Length of the flux history used for the adaptive threshold, in frames.
   * 29 frames is ~310 ms at 48 kHz: long enough that one strum's spike cannot
   * dominate the statistics and lock the detector out of the next strum, short
   * enough to follow a change of room or playing dynamics.
   */
  medianWindow: 29,
  /**
   * A peak must exceed `median + madMultiplier * MAD` of the recent flux.
   *
   * Median and median-absolute-deviation rather than mean and standard
   * deviation: the window deliberately contains onset spikes, and a mean would
   * be dragged upward by exactly the events it is supposed to detect.
   */
  madMultiplier: 6,
  /** Absolute floor, so a dead-silent room cannot make the threshold zero. */
  thresholdFloor: 0.02,
  /**
   * A peak must also reach this fraction of how strong recent onsets were.
   *
   * The decaying tail of a chord produces small flux bumps as its partials beat
   * against each other. They are real spectral change, so no purely statistical
   * threshold rejects them — but they are an order of magnitude weaker than an
   * actual strum, which this catches.
   */
  relativeFloor: 0.45,
  /** Per-frame decay of that running peak (~1 s memory at 48 kHz). */
  peakDecay: 0.989,
  /**
   * After a peak is confirmed, walk back to where the attack actually started:
   * the earliest recent frame still holding this fraction of the peak.
   * Peak position drifts by tens of milliseconds with how hard the chord was
   * hit; the attack's leading edge does not, and that is what a listener hears
   * as "the beat".
   */
  backtrackFraction: 0.4,
  /** How many frames back the search for the attack start may go. */
  backtrackFrames: 8,
  /** Minimum gap between onsets. One strum is one event, not six. */
  refractoryMs: 120,
  /** Frames of flux history kept before the detector reports anything. */
  warmupFrames: 8,
} as const;

export const CHROMA = {
  /** Native rate is divided by this before the harmonic FFT. */
  decimation: 4,
  /** FFT over the decimated signal. 8192 @12k = 682 ms window. */
  fftSize: 8192,
  /** Analysis stride in decimated samples. 1024 @12k = 85 ms. */
  hopSize: 1024,
  /** Lowest pitch considered — a semitone below the open low E (82.41 Hz). */
  minMidi: 39,
  /** Highest pitch considered — high partials add noise, not information. */
  maxMidi: 88,
  /** Harmonics summed per note. 1 = fundamental only. */
  harmonics: 3,
  /** Weight applied to the n-th harmonic (index 0 = fundamental). */
  harmonicWeights: [1, 0.55, 0.3] as const,
  /** Half-width of the bin search window, in cents, around each partial. */
  searchCents: 45,
  /** Spectral subtraction keeps at least this fraction of the raw magnitude. */
  noiseSubtractFloor: 0.08,
  /** Over-subtraction factor for the measured noise spectrum. */
  noiseSubtractAlpha: 1.6,
} as const;

export const CALIBRATION = {
  /** Seconds of input measured before a session starts. */
  durationSec: 1.2,
  /** Above this the room is called noisy and the user is warned. */
  noiseFloorWarnDb: -42,
  /** Below this we assume the mic is not picking anything up at all. */
  noiseFloorSilentDb: -85,
  /** Fraction of clipped samples that makes us warn about input level. */
  clipRatioWarn: 0.002,
  /** Minimum headroom between the floor and full scale to call it usable. */
  minHeadroomDb: 20,
} as const;

export const SCORING = {
  /** Weights of the four target-score terms. Must sum to 1. */
  weights: {
    purity: 0.5,
    coverage: 0.3,
    root: 0.12,
    bass: 0.08,
  },
  /**
   * Purity is remapped from [floor, ceiling] onto [0, 1] before weighting.
   *
   * The raw ratio is badly compressed: playing C against a target of Em still
   * scores 0.58, because C and Em share two notes and the third leaks in as a
   * harmonic. Stretching the band that actually separates chords turns a
   * 0.58-vs-0.80 difference into 0.42-vs-0.82.
   */
  purityFloor: 0.35,
  purityCeiling: 0.9,
  /**
   * A pitch class counts as "covered" once it holds this share of the energy
   * an even distribution over the expected classes would give it.
   *
   * Deliberately strict. At 0.45 a trace of harmonic leakage counted as a fully
   * present chord tone, which let C score 0.99 coverage against Em — the B it
   * "had" was only the third harmonic of its own E.
   */
  coverageShare: 0.7,
  /** Extra weight the root and the bass pitch class carry inside `purity`. */
  rootWeight: 1.35,
  bassWeight: 1.2,
  /** Share of the strongest pitch class at which the root term saturates. */
  rootFullShare: 0.7,

  /** Bass term, in dB over the calibrated noise floor. */
  bass: {
    /** At or below this the expected bass is considered absent. */
    absentDb: 5,
    /** At or above this it is considered clearly present. */
    presentDb: 16,
    /** A muted string may exceed the intended bass by this much for free. */
    toleratedExcessDb: 2,
    /** Excess beyond that, over this range in dB, drives the term to zero. */
    excessRangeDb: 10,
  },
  /** Number of frames the median smoother keeps. 5 @12 fps ≈ 0.4 s. */
  smoothingFrames: 5,

  /** Verdict thresholds on the smoothed score. */
  correctScore: 0.76,
  almostScore: 0.6,

  /** Below this confidence the verdict is `uncertain`, whatever the score. */
  uncertainConfidence: 0.42,

  /** Confidence model. */
  confidence: {
    /** SNR in dB at which the SNR term reaches 1. */
    fullSnrDb: 26,
    /** SNR in dB below which the SNR term is 0. */
    zeroSnrDb: 7,
    /** Tonalness at which the tonalness term reaches 1. */
    fullTonalness: 0.55,
    /** Score standard deviation over the smoothing window that halves trust. */
    unstableStdDev: 0.13,
    /** Multiplier applied when the frame clipped. */
    clippedPenalty: 0.55,
    weights: { snr: 0.4, tonalness: 0.25, stability: 0.35 },
  },

  /**
   * Note coverage — octave-specific evidence that the *voicing* was played.
   *
   * Chroma coverage counts pitch classes, and a single string's harmonics
   * cover the whole triad's pitch classes on their own (an open A string
   * sounds A, then E and C# as partials): measured, one A2 string scored
   * 0.93 "correct" against the full A chord. What a single string cannot do
   * is produce the chord's *other* notes in their own octaves — E3 and C#4
   * for that A chord are not partials of A2. So each expected note's own
   * fundamental is checked for presence, and a verdict is capped at "almost"
   * unless enough of them are actually there.
   */
  noteCoverage: {
    /** dB a note's fundamental must sit above its local spectral shelf. */
    presentDb: 6,
    /** Low fundamentals sit in phone-mic roll-off; they get a lower bar. */
    lowNotePresentDb: 3,
    lowNoteMaxHz: 120,
    /** Half-width in cents of the bin window searched around each note. */
    searchCents: 40,
    /** Bins either side used to estimate the local spectral shelf. */
    backgroundSpread: 14,
    /** Share of expected notes that must be present for "correct". */
    minForCorrect: 0.7,
  },
  /** Blind detection only speaks when it is this much better than runner-up. */
  blindDetectionMargin: 0.08,
  /** …and at least this good in absolute terms. */
  blindDetectionMinScore: 0.55,
} as const;

export const STRINGS = {
  /** Partials examined per string (fundamental + harmonics). */
  partials: 4,
  /** Two partials closer than this are treated as inseparable. */
  partialCollisionCents: 35,
  /** dB over the noise floor for a partial to count as present. */
  presentSnrDb: 11,
  /** dB over the noise floor below which the string is called silent. */
  silentSnrDb: 5,
  /** Low strings need extra margin — phone mics roll off below ~100 Hz. */
  lowStringExtraDb: 4,
  /** Frequency under which the extra margin applies. */
  lowStringMaxHz: 120,
  /** A muted (x) string is called `unwanted` above this SNR. */
  unwantedSnrDb: 14,
  /** Minimum per-string confidence before the UI is allowed to mention it. */
  reportConfidence: 0.55,
  /** Never show more than this many issues at once. */
  maxReportedIssues: 3,
  /** Half-width in cents of the bin window searched around each partial. */
  searchCents: 40,
  /** Bins either side of a partial used to estimate the local spectral shelf. */
  backgroundSpread: 14,
  /** Multiplier when a string's only usable partials sit in the mic roll-off. */
  lowOnlyConfidencePenalty: 0.6,
  /** Credit a `weak` string earns towards the clarity score. */
  weakStringCredit: 0.5,
  /** Clarity penalty per unwanted (should-be-muted) string that rang. */
  unwantedPenalty: 0.22,
} as const;

export const GATE = {
  /**
   * Post-onset evidence required before an event can be decided, in seconds.
   * The tap-versus-restrike discrimination needs the sustain window nearly
   * full: at 0.17 s a tap over a ringing chord measures a level ratio of
   * ~0.99 (indistinguishable from a re-strum's ~1.02); at 0.28 s it has
   * fallen to ~0.93 and separates cleanly. The window also reaches past the
   * length of a spoken syllable: a vowel is over by ~250 ms and its sustain
   * evidence collapses, where a string is still ringing.
   */
  minEvidenceSec: 0.31,
  /**
   * Upper bound on how long an event decision may take (evidence window plus
   * frame cadence). Rhythm scoring delays beat resolution by this much so a
   * gated onset still arrives while its beat is pending — with its original
   * timestamp, so the delay never becomes timing error.
   */
  eventDecisionSec: 0.45,
  /**
   * A pluck's envelope reaches most of its peak within this of the attack.
   * Measured as time-to-reach `fastAttackFraction` × peak, not the position of
   * the absolute maximum — partials beating against each other ripple a
   * ringing chord's envelope, and the literal maximum wanders. Voices swell.
   */
  fastAttackSec: 0.055,
  fastAttackFraction: 0.8,
  /**
   * After the attack peak, the envelope may not re-rise beyond peak × this.
   * A consonant-then-vowel syllable re-rises; a plucked string never does.
   */
  reRiseRatio: 1.15,
  /** 140–320 ms after the attack, the envelope must still hold this fraction of the peak. */
  sustainFraction: 0.12,
  /** …and sit at least this multiple of the calibrated noise floor. */
  sustainFloorMultiple: 2.5,
  /** Pre-onset level above floor × this means "something was already ringing". */
  ringingContextMultiple: 3,
  /**
   * Over a ringing context, a re-strike is told from a tap by the *frame* RMS
   * — the 682 ms window integral, which partial-beating ripple cannot touch.
   * Fresh strum energy refills the window and its mean rises; a tap leaves
   * the old decay running and the mean keeps falling (~0.85 over the decision
   * span at a typical decay). Hop-level ratios were tried twice and measured
   * inseparable either way; this is the version with margins on both sides.
   */
  restrikeFrameRatio: 0.97,
  /**
   * Post-attack chroma concentration required for a guitar event: the share
   * of chroma energy in the top three pitch classes. This is a *noise* check,
   * not a speech check: measured at event-resolution time, broadband noise
   * sits at ≤0.31 while chords start at 0.36 and climb — but speech vowels
   * reach 0.5+, so the threshold is set low and vowels are handled by the
   * dip-and-return and voice-glide checks instead.
   */
  minEventSparsity: 0.34,
  /**
   * The voice veto. Voiced speech is periodic — YIN reads it with high clarity,
   * exactly like a single guitar string — but its pitch *glides* (intonation is
   * what makes speech speech), while a plucked string holds its pitch to a few
   * cents. A high-clarity source whose f0 moves more than `voiceGlideCents`
   * between analysis frames is a voice, whatever its envelope did.
   */
  voiceClarity: 0.6,
  voiceGlideCents: 25,
  /**
   * Glides at or beyond this are ignored by the veto: YIN on a chord flips
   * between octave candidates of the same pitch, and an exact-octave "glide"
   * is a detector artifact, not intonation. Speech glides measure tens to
   * hundreds of cents across the veto's span on the probe fixtures — squarely
   * inside the band. The long YIN window smooths within-vowel motion, so the
   * veto checks both adjacent readings and the whole span of recent ones.
   */
  voiceGlideMaxCents: 950,
  /** How long a confirmed pluck keeps the frame gate in the guitar state. */
  activeHoldSec: 2.5,
  /**
   * Past this point of the hold, the guitar state additionally requires a
   * genuinely decaying envelope. A struck string cannot do anything else; a
   * voice riding a phantom confirm sustains or swells — measured on
   * continuous speech, which produces too few onset candidates for the
   * rejection context to build, leaving the envelope as the only tell.
   */
  activeDecayAfterSec: 0.9,
  /**
   * Chroma cosine (one frame back) required for a guitar frame. The shared
   * analysis window overlaps heavily between frames, so a ringing chord sits
   * at 0.96–1.0; anything below this is content that is actively changing.
   */
  frameStability: 0.9,
  /**
   * The ring-hold: a decaying, tonal, frozen ring may stay "guitar" after the
   * pluck hold expires (a chord held in Clean Chords rings for seconds). All
   * three must hold — overlap inflation alone pushes stability to ~0.99 for
   * nearly any sound, which is why stability by itself proves nothing.
   */
  ringHoldStability: 0.97,
  ringHoldTonalness: 0.6,
  ringHoldFloorMultiple: 3,
  /** A candidate rejected this recently demotes the guitar state to uncertain. */
  rejectedHoldSec: 0.45,
  /**
   * The dip-and-return check: a consonant-then-vowel syllable collapses after
   * its burst and rises again; a pluck's envelope never collapses and returns.
   * Compared on medians — partials beating against each other ripple a real
   * chord's envelope, and a min-vs-max comparison trips on its own troughs.
   */
  dipReturnRatio: 1.5,
  /**
   * The speech-context suppressor. Continuous talking produces a stream of
   * voice-signature rejections (glides, re-rises, dips, slow attacks); while
   * at least `contextRejections` of them sit inside `contextWindowSec`, new
   * candidates are rejected outright — mid-sentence, every "attack" is a
   * syllable. Percussive rejections (taps, noise) do not build this context,
   * and a confirmed pluck clears it, so playing is never locked out by its
   * own strums. The cost is deliberate and conservative: after continuous
   * talking, the first strum inside the window is dropped too.
   */
  contextWindowSec: 1.2,
  contextRejections: 2,
  /**
   * A candidate whose surroundings triggered the frame-level voice veto —
   * within this long before the attack, or at any point before the decision —
   * is a voice event, whatever its own envelope did. Measured: when talking
   * starts, the first "attack" can land on an f0 plateau and pass every local
   * check while the frames around it are already flagged as gliding voice.
   */
  voiceProximitySec: 0.25,
  /** Tonalness under this on an audible frame is broadband — rustle, noise, movement. */
  broadbandTonalness: 0.2,
  /** Tonalness above this without any pluck behind it is a hum, a TV, a voice. */
  sustainedTonalness: 0.5,
  /** RMS under floor × this is silence — nothing to classify. */
  silenceFloorMultiple: 1.8,
  /**
   * Decay test window and threshold, measured on **frame RMS** — the 682 ms
   * window integral. Hop-level envelopes were tried and failed the same way
   * twice before: partials beating against each other make 40–50 % of
   * hop-to-hop steps "rises" on a genuinely decaying chord. The frame
   * integral is immune. Over ~0.7 s a τ≈1.5 s guitar decay loses ~35 %; a
   * voice or hum sustains.
   */
  decayWindowSec: 0.7,
  decayEndRatio: 0.9,
  /** Consecutive frames that must agree before the reported kind switches. */
  kindSmoothingFrames: 2,
} as const;

export const TUNER = {
  /** Lowest fundamental the tuner will report. Drop-D players tune to D2 (73.4 Hz). */
  minFrequencyHz: 65,
  /** Highest fundamental. Covers fretted checks well above the open high E. */
  maxFrequencyHz: 700,
  /** YIN integration window in decimated samples. 2048 @12 kHz ≈ 171 ms. */
  windowSize: 2048,
  /** CMNDF absolute threshold (the paper suggests ~0.1; guitars are cleaner). */
  threshold: 0.12,
  /** Below this clarity a reading is noise and is not shown. */
  minClarity: 0.75,
  /** RMS below this is silence: no reading, needle at rest. */
  minRms: 0.004,
  /** Readings kept for the median that steadies the display. */
  smoothingFrames: 5,
  /** |cents| at or under this counts as in tune (shown green). */
  inTuneCents: 5,
  /** The needle's full scale, in cents either side of the target. */
  displayRangeCents: 50,
} as const;

export const METRONOME = {
  /** Scheduler wake-up interval in ms. */
  lookaheadMs: 25,
  /** How far ahead of the audio clock clicks are scheduled, in seconds. */
  scheduleAheadSec: 0.15,
  /** Click envelope. */
  click: {
    accentFrequency: 1400,
    normalFrequency: 900,
    durationSec: 0.035,
    attackSec: 0.001,
    gain: 0.32,
  },
} as const;

/** Guard rails for the tempo control. */
export const BPM_RANGE = { min: 30, max: 200, step: 1 } as const;
